import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc, query, orderBy, getDoc, setDoc } from 'firebase/firestore';
import { ref, listAll, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';
import { markStudioClaimsAsSeen } from './hooks/useUnseenClaims';

const AdminStudioClaimReview = () => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [studioDocuments, setStudioDocuments] = useState(new Map());
  
  // Rejection reasons state
  const [rejectionReasons, setRejectionReasons] = useState(new Map()); // claimId -> {reasons: [], showDialog: boolean}

  useEffect(() => {
    fetchClaims();
    fetchStudioDocuments();
  }, []);

  const fetchClaims = async () => {
    try {
      const q = query(collection(db, 'studioProfileClaims'), orderBy('submittedAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const claimsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setClaims(claimsData);
      
      // Mark all pending studio claims as seen when admin views the page
      const pendingClaimIds = claimsData
        .filter(claim => claim.status === 'pending')
        .map(claim => claim.id);
      
      if (pendingClaimIds.length > 0) {
        markStudioClaimsAsSeen(pendingClaimIds);
      }
      
      console.log('📄 Studio Claims loaded:', claimsData.length);
    } catch (error) {
      console.error('Error fetching studio claims:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudioDocuments = async () => {
    try {
      const documentsMap = new Map();
      const verificationDocsRef = ref(storage, 'verification-docs');
      const folderList = await listAll(verificationDocsRef);
      
      for (const folderRef of folderList.prefixes) {
        const studioName = folderRef.name;
        const fileList = await listAll(folderRef);
        const files = await Promise.all(
          fileList.items.map(async (item) => {
            try {
              const url = await getDownloadURL(item);
              return {
                name: item.name,
                fullPath: item.fullPath,
                url,
                source: 'storage-verification'
              };
            } catch (error) {
              console.error(`Error getting URL for ${item.fullPath}:`, error);
              return {
                name: item.name,
                fullPath: item.fullPath,
                url: null,
                source: 'storage-verification',
                error: error.message
              };
            }
          })
        );
        documentsMap.set(studioName, files);
      }
      
      setStudioDocuments(documentsMap);
      console.log('📁 Studio Documents loaded for folders:', Array.from(documentsMap.keys()));
    } catch (error) {
      console.error('Error fetching studio documents:', error);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      let date;
      if (timestamp.toDate) {
        date = timestamp.toDate();
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else {
        date = new Date(timestamp);
      }
      
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { backgroundColor: '#fff3cd', color: '#856404', border: '1px solid #ffeaa7' },
      approved: { backgroundColor: '#d4edda', color: '#155724', border: '1px solid #c3e6cb' },
      rejected: { backgroundColor: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb' }
    };
    
    return (
      <span style={{
        padding: '0.25rem 0.5rem',
        borderRadius: '4px',
        fontSize: '0.8rem',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        ...styles[status]
      }}>
        {status}
      </span>
    );
  };

  const handleApproveClaim = async (claimId, studioName, userId) => {
    try {
      // Get the claim data first
      const claimRef = doc(db, 'studioProfileClaims', claimId);
      const claimSnap = await getDoc(claimRef);
      
      if (!claimSnap.exists()) {
        console.error(`Claim ${claimId} not found`);
        return;
      }
      
      const claimData = claimSnap.data();
      
      // Update claim status to approved
      await updateDoc(claimRef, {
        status: 'approved',
        reviewedAt: new Date()
      });
      
      // Create or update studio profile data
      const studioRef = doc(db, 'studioProfiles', studioName);
      
      // Prepare data to save in studio profile
      const profileData = {
        profileClaimed: true,
        claimedBy: {
          userId: claimData.userId,
          email: claimData.userEmail,
          approvedAt: new Date()
        },
        claimedAt: new Date(),
        displayName: claimData.displayName || studioName, // Use display name from claim or default to original studio name
        yearEstablished: claimData.yearEstablished || '',
        website: claimData.website || '',
        description: claimData.description || '',
        logoURL: claimData.logoURL || '',
        socialMedia: claimData.socialMedia || {
          instagram: '',
          facebook: '',
          tiktok: ''
        },
        lastUpdated: new Date()
      };
      
      // Save to studioProfiles collection
      await setDoc(studioRef, profileData, { merge: true });
      console.log(`✅ Updated/Created studioProfiles document for ${studioName}`);
      
      console.log(`✅ Studio claim approved: ${studioName} for user ${userId}`);
      fetchClaims(); // Refresh the list
    } catch (error) {
      console.error('Error approving studio claim:', error);
    }
  };

  const handleRejectClaim = async (claimId) => {
    // Show rejection dialog
    console.log('handleRejectClaim called for:', claimId);
    const reasons = rejectionReasons.get(claimId) || { reasons: [], showDialog: false };
    const newMap = new Map(rejectionReasons);
    newMap.set(claimId, { ...reasons, showDialog: true });
    console.log('Setting showDialog to true for claim:', claimId, newMap.get(claimId));
    setRejectionReasons(newMap);
  };

  const confirmRejectClaim = async (claimId) => {
    try {
      const reasons = rejectionReasons.get(claimId);
      const customMessage = (reasons?.customMessage || '').trim();
      if (!reasons || (reasons.reasons.length === 0 && !customMessage)) {
        alert('Please select at least one rejection reason or write a custom message.');
        return;
      }

      // The onStudioClaimStatusUpdated Cloud Function e-mails these fields to the claimant
      await updateDoc(doc(db, 'studioProfileClaims', claimId), {
        status: 'rejected',
        reviewedAt: new Date(),
        reviewedBy: 'admin',
        rejectionReasons: reasons.reasons,
        rejectionMessage: customMessage
      });

      // Close dialog and clear reasons
      const newMap = new Map(rejectionReasons);
      newMap.set(claimId, { reasons: [], showDialog: false });
      setRejectionReasons(newMap);
      
      console.log(`❌ Studio claim rejected: ${claimId}`);
      fetchClaims(); // Refresh the list
    } catch (error) {
      console.error('Error rejecting studio claim:', error);
    }
  };

  const cancelRejectClaim = (claimId) => {
    const newMap = new Map(rejectionReasons);
    newMap.set(claimId, { reasons: [], showDialog: false });
    setRejectionReasons(newMap);
  };

  const toggleRejectionReason = (claimId, reason) => {
    const current = rejectionReasons.get(claimId) || { reasons: [], showDialog: true };
    const updatedReasons = current.reasons.includes(reason)
      ? current.reasons.filter(r => r !== reason)
      : [...current.reasons, reason];
    
    const newMap = new Map(rejectionReasons);
    newMap.set(claimId, { ...current, reasons: updatedReasons });
    setRejectionReasons(newMap);
  };

  const setRejectionMessage = (claimId, customMessage) => {
    const current = rejectionReasons.get(claimId) || { reasons: [], showDialog: true };
    const newMap = new Map(rejectionReasons);
    newMap.set(claimId, { ...current, customMessage });
    setRejectionReasons(newMap);
  };

  const handleChangeStatus = async (claimId, newStatus, studioName, userId) => {
    try {
      await updateDoc(doc(db, 'studioProfileClaims', claimId), {
        status: newStatus,
        reviewedAt: new Date()
      });
      
      console.log(`🔄 Studio claim status changed to ${newStatus}: ${studioName} for user ${userId}`);
      fetchClaims(); // Refresh the list
    } catch (error) {
      console.error('Error changing studio claim status:', error);
    }
  };

  const handleDeleteClaim = async (claimId) => {
    if (window.confirm('Are you sure you want to delete this studio claim? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'studioProfileClaims', claimId));
        console.log(`🗑️ Studio claim deleted: ${claimId}`);
        fetchClaims(); // Refresh the list
      } catch (error) {
        console.error('Error deleting studio claim:', error);
      }
    }
  };

  const renderDocumentViewer = (claim) => {
    if (!claim.documentURL) {
      return (
        <div className="claim-field">
          <strong>Document:</strong> Not available
        </div>
      );
    }

    const isImage = claim.documentURL.includes('image') || 
                   claim.documentFileName?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
    const isPDF = claim.documentURL.includes('pdf') || 
                  claim.documentFileName?.match(/\.pdf$/i);

    return (
      <div className="claim-field">
        <strong>Verification Document:</strong>
        <div style={{ marginTop: '0.5rem' }}>
          {isImage ? (
            <img 
              src={claim.documentURL} 
              alt="Verification Document"
              style={{ 
                maxWidth: '300px', 
                maxHeight: '400px', 
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
          ) : isPDF ? (
            <embed 
              src={claim.documentURL} 
              type="application/pdf" 
              width="100%" 
              height="400px"
              style={{ border: '1px solid #ddd', borderRadius: '4px' }}
            />
          ) : (
            <div style={{ 
              padding: '1rem', 
              backgroundColor: '#f8f9fa', 
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}>
              <p>📄 Document type not supported for preview</p>
            </div>
          )}
          <div style={{ display: 'none', color: 'red', marginTop: '0.5rem' }}>
            ❌ Failed to load document. URL: {claim.documentURL}
          </div>
          <div style={{ marginTop: '0.5rem' }}>
            <a 
              href={claim.documentURL} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                color: '#007bff', 
                textDecoration: 'none',
                fontSize: '0.9rem'
              }}
            >
              🔗 Open document in new tab
            </a>
          </div>
        </div>
      </div>
    );
  };

  const filteredClaims = statusFilter === 'all' 
    ? claims 
    : claims.filter(claim => claim.status === statusFilter);

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading studio claims...</div>;
  }

  return (
    <div style={{ padding: '1rem' }}>
      <h2>🏢 Studio Profile Claims Review</h2>
      
      <div style={{ 
        marginBottom: '1rem', 
        display: 'flex', 
        gap: '1rem', 
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        <div>
          <label htmlFor="statusFilter" style={{ marginRight: '0.5rem' }}>Filter by status:</label>
          <select 
            id="statusFilter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ 
              padding: '0.5rem',
              borderRadius: '4px',
              border: '1px solid #ddd'
            }}
          >
            <option value="all">All Claims ({claims.length})</option>
            <option value="pending">Pending ({claims.filter(c => c.status === 'pending').length})</option>
            <option value="approved">Approved ({claims.filter(c => c.status === 'approved').length})</option>
            <option value="rejected">Rejected ({claims.filter(c => c.status === 'rejected').length})</option>
          </select>
        </div>
        
        <button
          onClick={fetchClaims}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          🔄 Refresh
        </button>
      </div>

      {claims.length === 0 ? (
        <div className="no-claims">
          No {statusFilter === 'all' ? '' : statusFilter} claims to show.
        </div>
      ) : (
        <div className="claims-list">
          {filteredClaims.map((claim) => (
            <div key={claim.id} className="claim-item" style={{
              border: '1px solid #ddd',
              borderRadius: '8px',
              padding: '1.5rem',
              marginBottom: '1rem',
              backgroundColor: 'white'
            }}>
              <div className="claim-header" style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <h3 style={{ margin: 0 }}>{claim.studioName}</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  {getStatusBadge(claim.status)}
                  <span className="claim-date" style={{ fontSize: '0.9rem', color: '#666' }}>
                    Submitted: {formatDate(claim.submittedAt)}
                  </span>
                </div>
              </div>

              {/* Quick Actions for Pending Claims */}
              {claim.status === 'pending' && (
                <div style={{
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: '6px',
                  padding: '1rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <strong style={{ color: '#856404' }}>⚡ Quick Decision</strong>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => handleApproveClaim(claim.id, claim.studioName, claim.userId)}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        ✅ APPROVE
                      </button>
                      <button
                        onClick={() => handleRejectClaim(claim.id)}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        ❌ REJECT
                      </button>
                    </div>
                  </div>
                  <small style={{ color: '#856404' }}>
                    Review the document below, then make your decision
                  </small>
                </div>
              )}

              {/* Rejection Reasons Dialog for Studio Claims */}
              {rejectionReasons.get(claim.id)?.showDialog && (
                <div style={{
                  backgroundColor: '#fff',
                  border: '2px solid #dc3545',
                  borderRadius: '8px',
                  padding: '1.5rem',
                  marginBottom: '1rem',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}>
                  <h4 style={{ color: '#dc3545', marginTop: 0 }}>Select Rejection Reason(s)</h4>
                  <p style={{ color: '#666', fontSize: '0.9rem' }}>Choose a reason and/or write a custom message. Both are e-mailed to the claimant:</p>
                  
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.75rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={rejectionReasons.get(claim.id)?.reasons.includes('Incorrect Verification Document') || false}
                        onChange={() => toggleRejectionReason(claim.id, 'Incorrect Verification Document')}
                        style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                      />
                      <strong>Incorrect Verification Document</strong> - Document provided is not valid or does not match the studio
                    </label>
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 'bold' }}>
                      Custom message to the claimant (optional)
                    </label>
                    <textarea
                      value={rejectionReasons.get(claim.id)?.customMessage || ''}
                      onChange={(e) => setRejectionMessage(claim.id, e.target.value)}
                      maxLength={1000}
                      rows={4}
                      placeholder="e.g. The document does not show the studio name. Please upload the registration certificate."
                      style={{ width: '100%', boxSizing: 'border-box', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc', fontFamily: 'inherit', fontSize: '0.9rem', resize: 'vertical' }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => cancelRejectClaim(claim.id)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => confirmRejectClaim(claim.id)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      Confirm Rejection
                    </button>
                  </div>
                </div>
              )}
              
              <div className="claim-details">
                <div className="claim-field">
                  <strong>User Email:</strong> {claim.userEmail}
                </div>
                <div className="claim-field">
                  <strong>User ID:</strong> {claim.userId || 'Not provided'}
                </div>
                {claim.documentType && (
                  <div className="claim-field">
                    <strong>Document Type:</strong> {claim.documentType}
                  </div>
                )}
                
                {/* Enhanced Studio Info Display */}
                <div style={{
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginTop: '1rem',
                  marginBottom: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem'
                }}>
                  <h4 style={{ margin: 0, color: '#333', borderBottom: '1px solid #ddd', paddingBottom: '0.5rem' }}>
                    Studio Profile Information
                  </h4>
                  
                  {/* Logo Preview */}
                  {claim.logoURL && (
                    <div className="claim-field">
                      <strong>Studio Logo:</strong>
                      <div style={{ marginTop: '0.5rem' }}>
                        <img 
                          src={claim.logoURL} 
                          alt="Studio Logo"
                          style={{ 
                            maxWidth: '150px',
                            maxHeight: '150px',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            objectFit: 'contain',
                            backgroundColor: 'white'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        <div style={{ display: 'none', color: 'red', marginTop: '0.5rem' }}>
                          ❌ Failed to load logo image
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Year Established */}
                  {claim.yearEstablished && (
                    <div className="claim-field">
                      <strong>Year Established:</strong> {claim.yearEstablished}
                    </div>
                  )}
                  
                  {/* Website */}
                  {claim.website && (
                    <div className="claim-field">
                      <strong>Website:</strong> 
                      <a 
                        href={claim.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ marginLeft: '0.25rem', color: '#007bff' }}
                      >
                        {claim.website}
                      </a>
                    </div>
                  )}
                  
                  {/* Social Media Links */}
                  {claim.socialMedia && Object.values(claim.socialMedia).some(val => val) && (
                    <div className="claim-field">
                      <strong>Social Media:</strong>
                      <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {claim.socialMedia.instagram && (
                          <a 
                            href={`https://instagram.com/${claim.socialMedia.instagram.replace('@', '')}`}
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{
                              padding: '0.3rem 0.6rem',
                              backgroundColor: '#e1306c',
                              color: 'white',
                              textDecoration: 'none',
                              borderRadius: '4px',
                              fontSize: '0.9rem',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}
                          >
                            📷 Instagram: @{claim.socialMedia.instagram.replace('@', '')}
                          </a>
                        )}
                        
                        {claim.socialMedia.facebook && (
                          <a 
                            href={claim.socialMedia.facebook}
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{
                              padding: '0.3rem 0.6rem',
                              backgroundColor: '#3b5998',
                              color: 'white',
                              textDecoration: 'none',
                              borderRadius: '4px',
                              fontSize: '0.9rem',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}
                          >
                            📘 Facebook
                          </a>
                        )}
                        
                        {claim.socialMedia.tiktok && (
                          <a 
                            href={`https://tiktok.com/@${claim.socialMedia.tiktok.replace('@', '')}`}
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{
                              padding: '0.3rem 0.6rem',
                              backgroundColor: '#000000',
                              color: 'white',
                              textDecoration: 'none',
                              borderRadius: '4px',
                              fontSize: '0.9rem',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}
                          >
                            🎵 TikTok: @{claim.socialMedia.tiktok.replace('@', '')}
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Description */}
                  {claim.description && (
                    <div className="claim-field">
                      <strong>Description:</strong>
                      <p style={{ 
                        margin: '0.5rem 0 0 0',
                        color: '#555',
                        maxHeight: '150px',
                        overflow: 'auto',
                        padding: '0.5rem',
                        backgroundColor: 'white',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px'
                      }}>
                        {claim.description.length > 300 
                          ? claim.description.substring(0, 300) + '...' 
                          : claim.description
                        }
                        {claim.description.length > 300 && (
                          <button
                            style={{
                              display: 'block',
                              marginTop: '0.5rem',
                              backgroundColor: 'transparent',
                              border: 'none',
                              color: '#007bff',
                              cursor: 'pointer',
                              padding: 0
                            }}
                            onClick={() => alert(claim.description)}
                          >
                            Read full description
                          </button>
                        )}
                      </p>
                    </div>
                  )}
                </div>
                
                {renderDocumentViewer(claim)}
                
                {/* Debug Information */}
                <details style={{ marginTop: '1rem', fontSize: '0.8rem' }}>
                  <summary style={{ cursor: 'pointer', color: '#666' }}>🔍 Debug Info</summary>
                  <pre style={{ 
                    backgroundColor: '#f8f9fa', 
                    padding: '0.5rem', 
                    borderRadius: '4px', 
                    marginTop: '0.5rem',
                    fontSize: '0.75rem',
                    overflow: 'auto'
                  }}>
                    {JSON.stringify({
                      claimId: claim.id,
                      studioName: claim.studioName,
                      sanitizedName: claim.studioName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_'),
                      status: claim.status,
                      // Document information
                      documentURL: claim.documentURL,
                      documentPath: claim.documentPath,
                      documentFileName: claim.documentFileName,
                      documentType: claim.documentType,
                      // Enhanced fields
                      yearEstablished: claim.yearEstablished || null,
                      website: claim.website || null,
                      description: claim.description ? `${claim.description.substring(0, 50)}...` : null,
                      logoURL: claim.logoURL || null,
                      logoPath: claim.logoPath || null,
                      socialMedia: claim.socialMedia || null,
                      // Storage information
                      firebaseStorageDocs: studioDocuments.get(claim.studioName)?.map(doc => ({ 
                        name: doc.name, 
                        path: doc.fullPath,
                        source: doc.source 
                      })) || 'None found',
                      expectedFolder: `verification-docs/${claim.studioName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_')}/`,
                      // Timestamps
                      submittedAt: claim.submittedAt?.toDate?.()?.toISOString() || claim.submittedAt,
                      reviewedAt: claim.reviewedAt?.toDate?.()?.toISOString() || claim.reviewedAt
                    }, null, 2)}
                  </pre>
                </details>
              </div>
              
              <div className="claim-actions" style={{ 
                display: 'flex', 
                gap: '0.5rem', 
                marginTop: '1rem',
                flexWrap: 'wrap'
              }}>
                {claim.status === 'pending' && (
                  <>
                    <button
                      className="approve-btn"
                      onClick={() => handleApproveClaim(claim.id, claim.studioName, claim.userId)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      ✅ Approve
                    </button>
                    <button
                      className="reject-btn"
                      onClick={() => handleRejectClaim(claim.id)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      ❌ Reject
                    </button>
                  </>
                )}
                
                {claim.status === 'approved' && (
                  <button
                    onClick={() => handleChangeStatus(claim.id, 'rejected', claim.studioName, claim.userId)}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#ffc107',
                      color: '#212529',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    🔄 Change to Rejected
                  </button>
                )}
                
                {claim.status === 'rejected' && (
                  <button
                    onClick={() => handleChangeStatus(claim.id, 'approved', claim.studioName, claim.userId)}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#17a2b8',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    🔄 Change to Approved
                  </button>
                )}

                <button
                  className="delete-btn"
                  onClick={() => handleDeleteClaim(claim.id)}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminStudioClaimReview;
